import type { FunctionComponent } from 'react';
import { useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { DropdownMenuItem } from '~/ui/core/dropdown-menu/DropdownMenu';
import { SplitDropdownButton } from '~/ui/core/split-dropdown-button/SplitDropdownButton';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { Spinner } from '../../components/Spinner';

export type SaveOptions = { readonly withAutoTranslation: boolean };

/**
 * Split Save control shared across the job-architecture editing surfaces
 * (job-profile role/level, job family, job cluster).
 */
export const SaveWithAutoTranslationButton: FunctionComponent<{
    readonly disabled?: boolean;
    readonly onSave: (options: SaveOptions) => Promise<void>;
}> = ({ disabled = false, onSave }) => {
    const translate = useTranslate();
    const { showErrorSnackbar } = useSnackbars();
    const [isSaving, setIsSaving] = useState(false);

    const runSave = (options: SaveOptions): void => {
        if (isSaving || disabled) return;
        setIsSaving(true);
        onSave(options)
            .catch(showErrorSnackbar)
            .finally(() => {
                setIsSaving(false);
            });
    };

    const saveLabel = translate('common.save');
    const label = (
        <span className="relative inline-flex items-center justify-center">
            <span className={isSaving ? 'invisible' : undefined}>{saveLabel}</span>
            {isSaving ? (
                <span className="absolute inset-0 flex items-center justify-center">
                    <Spinner />
                </span>
            ) : null}
        </span>
    );
    const withoutAutoTranslationLabel = translate(
        'pages.job-architecture-v2.job-profile-save-without-auto-translation',
    );

    return (
        <SplitDropdownButton
            menuLabel={withoutAutoTranslationLabel}
            primary={{
                children: label,
                color: 'default',
                disabled,
                onClick: () => runSave({ withAutoTranslation: true }),
                variant: 'solid',
            }}
        >
            <DropdownMenuItem disabled={disabled} onSelect={() => runSave({ withAutoTranslation: false })}>
                {withoutAutoTranslationLabel}
            </DropdownMenuItem>
        </SplitDropdownButton>
    );
};
